import * as React from 'react'
import Head from 'next/head'
import { GetServerSidePropsContext } from 'next'
import { useRouter } from 'next/router'
import getConfig from 'next/config'

import { GridItem } from '@thg-commerce/gravity-system'
import { isStoreOpen } from '@thg-commerce/enterprise-network/src/ApolloProvider/resolvers/Query/Content/StoreByStoreUrlTag'
import { processesLowerCase } from '@thg-commerce/enterprise-network/src/ApolloProvider/resolvers/Query/Content/Stores'
import { StoreDetails as StoreDetailsInformation } from '@thg-commerce/gravity-patterns/StoreDetails/StoreDetails'
import {
  ComponentName,
  ComponentWidgetsData,
} from '@thg-commerce/enterprise-network/src/ApolloProvider/resolvers/Query/ComponentWidgets'
import {
  Country,
  Currency,
  Feature,
  StoreOpeningTime,
} from '@thg-commerce/enterprise-network/src/generated/graphql'
import {
  Routes,
  PageWrapper,
  useSiteConfig,
  withCacheConfiguration,
  EnterpriseNextPage,
  useSiteDefinition,
  i18n,
} from '@thg-commerce/enterprise-core'
import { FullHeaderLayout } from '@thg-commerce/enterprise-core/src/Layouts'
import { PageType } from '@thg-commerce/enterprise-core/src/App/types'
import { loadConfiguration } from '@thg-commerce/enterprise-core/src/ConfigurationLoader/ConfigurationLoader'
import { NextConfig } from '@thg-commerce/enterprise-core/src/ConfigurationLoader/types'
import { EnterpriseRequest } from '@thg-commerce/enterprise-core/src/Server/types'
import { createApolloWithRequest } from '@thg-commerce/enterprise-core/src/Factory/apollo'
import { WidgetRenderer } from '@thg-commerce/enterprise-widgets'
import { StoreDetailsNearbyStores } from '@thg-commerce/gravity-patterns'
import { openGoogleDirectionMap } from '@thg-commerce/gravity-patterns/StoreMap/utils'
import { Resolvers } from '@thg-commerce/enterprise-network/src/ApolloProvider/resolvers'
import { BreadcrumbsRenderer } from '@thg-commerce/enterprise-components'

import { StoreDetails as STORE_DETAILS_QUERY } from '../../../../../src/graphql/StoreDetails.graphql'
import CustomError from '../../../../_error'
import { replaceString } from '../../../../../src/utils'

enum Status {
  OPEN = 'OPEN',
  CLOSED = 'CLOSED',
  PERMANENTLY_CLOSED = 'PERMANENTLY_CLOSED',
  TEMPORARILY_CLOSED = 'TEMPORARILY_CLOSED',
}

enum FacilityIcon {
  YES = 'YES',
  NO = 'NO',
}
interface StoreFacility {
  icon: FacilityIcon
  title: string
}
interface StoreDetailsData {
  store: {
    id: string
    displayName: string
    urlTag: string
    phoneNumber: string
    longitude: number
    latitude: number
    relativeDistance: number
    status: Status
    storeFacility: StoreFacility[]
    address: {
      country: Country
      addresseeName: string
      addressLine1: string
      addressLine2: string
      addressLine3?: string
      addressLine4: string
      addressLine5: string
      companyName: string
      state?: string
      phoneNumber: string
      postalCode: string
      clickAndCollect?: boolean
    }
    openingTimes: StoreOpeningTime[]
    nearbyStores?: StoreDetailsData['store'][]
  }
}

interface StoreDetailsProps {
  store?: StoreDetailsData['store']
  componentWidgets?: ComponentWidgetsData['componentWidgets']
}

const getBreadcrumbs = (
  storeAddress: StoreDetailsData['store']['address'],
  path: string,
  text: { home: string; region: string; storeFinder: string; urlTag: string },
) => {
  const { addressLine4, addressLine5, country } = storeAddress
  const townString = processesLowerCase(addressLine4)
  const regionString = processesLowerCase(addressLine5)
  const countryString = country === 'GB' ? 'uk' : processesLowerCase(country)

  if (path === 'indexPage') {
    return [
      {
        text: text.home,
        url: Routes.HomePage,
      },
      {
        text: text.region,
        url: Routes.Stores.replace(':country', countryString),
      },
      {
        text: replaceString(regionString) || '',
        url: Routes.StoresRegion.replace(':country', countryString).replace(
          ':region',
          regionString,
        ),
      },
      {
        text: replaceString(townString) || '',
        url: Routes.StoresCity.replace(':country', countryString)
          .replace(':region', regionString)
          .replace(':town', townString),
      },
      {
        text: replaceString(text.urlTag),
        url: Routes.StoresCity.replace(':city', text.urlTag),
      },
    ]
  }

  if (path === 'searchPage') {
    return [
      {
        text: text.home,
        url: Routes.HomePage,
      },
      {
        text: text.storeFinder,
        url: Routes.StoreLocator,
      },
      {
        text: replaceString(text.urlTag),
        url: Routes.StoresCity.replace(':city', text.urlTag),
      },
    ]
  }

  return []
}

const StoreDetails: EnterpriseNextPage<StoreDetailsProps> = (props) => {
  const { enableStoreLocator, geocodeApiKeyStoreMap } = useSiteConfig()
  const { originUrl } = useSiteDefinition()
  const router = useRouter()
  const {
    query: { town },
  } = router

  if (!enableStoreLocator || !props.store || !props.store.id) {
    return <CustomError statusCode={404} />
  }

  const { displayName } = props.store

  const i18nBreadcrumbs = {
    breadCrumbsHome: i18n('general.breadcrumbs.home.text'),
    storeFinder: i18n('storelocator.storedetails.breadcrumbs.storefinder'),
    storesRegions: i18n('breadcrumbs.all.stores'),
  }

  const storeDetailsCardI18nText = {
    storeOpenText: i18n('storelocator.storedetails.open'),
    storeClosedText: i18n('storelocator.storedetails.closed'),
    storeTempClosed: i18n('storelocator.storedetails.temporarilyclosed'),
    storePermClosed: i18n('storelocator.storedetails.permanentlyclosed'),
    storeDetailsButton: i18n('storelocator.storedetails.detailsbutton'),
    getDirectionsButton: i18n('storelocator.storedetails.directionsbutton'),
    milesText: i18n('storelocator.storedetails.distanceunits'),
  }

  const storeDetailsI18nText = {
    storeInfoTitleText: i18n('storelocator.storedetails.storeinfo.title'),
    storeOpeningTimesTitleText: i18n(
      'storelocator.storedetails.openingtimes.title',
    ),
    storeFacilitiesTitleText: i18n(
      'storelocator.storedetails.facilities.title',
    ),
    openText: i18n('storelocator.storedetails.openingtimes.open'),
    closedText: i18n('storelocator.storedetails.openingtimes.closed'),
    getDirectionsButtonText: i18n('storelocator.storedetails.directionsbutton'),
    distanceAwayText: i18n('storelocator.storedetails.distance'),
    storeOpenText: i18n('storelocator.storedetails.open'),
    storeClosedText: i18n('storelocator.storedetails.closed'),
    storeTempClosed: i18n('storelocator.storedetails.temporarilyclosed'),
    storePermClosed: i18n('storelocator.storedetails.permanentlyclosed'),
    email: i18n('storelocator.storedetails.email'),
    storeDetailsCard: storeDetailsCardI18nText,
    storeDetailsDescription: i18n('storelocator.storedetails.description.text'),
  }

  const nearbyStoresI18nText = {
    nearbyStoresTitle: i18n('storelocator.storedetails.nearbystores.title'),
    storeDetailsCard: storeDetailsCardI18nText,
  }

  const sessionStorage = typeof window !== 'undefined' && window.sessionStorage

  const breadcrumbItems = getBreadcrumbs(
    props.store.address,
    sessionStorage
      ? sessionStorage.getItem('storeDetailsPrevPath') || 'searchPage'
      : '',
    {
      home: i18nBreadcrumbs.breadCrumbsHome,
      region: i18nBreadcrumbs.storesRegions,
      storeFinder: i18nBreadcrumbs.storeFinder,
      urlTag: props.store.urlTag,
    },
  )

  const storeUrl = `${originUrl}${Routes.StoreDetails.replace(
    ':town',
    typeof town === 'string' ? town : '',
  ).replace(':store', props.store.urlTag)}`

  const storeSchema = {
    '@context': 'https://schema.org',
    '@type': 'DepartmentStore',
    name: props.store?.displayName,
    address: {
      '@type': 'PostalAddress',
      streetAddress: props.store?.address?.addressLine1,
      addressLocality: props.store?.address?.addressLine4,
      addressRegion: props.store?.address?.addressLine5,
      postalCode: props.store?.address?.postalCode,
      addressCountry: props?.store?.address?.country,
    },
    openingHoursSpecification: props.store?.openingTimes?.map((openingTime) => {
      return {
        '@type': 'OpeningHoursSpecification',
        closes: openingTime.closingTime,
        dayOfWeek: `https://schema.org/${openingTime.day.toLowerCase()}`,
        opens: openingTime.openingTime,
      }
    }),
    description: storeDetailsI18nText.storeDetailsDescription,
    telephone: props.store?.phoneNumber,
    url: storeUrl,
  }
  return (
    <React.Fragment>
      <Head>
        <title>{displayName}</title>
        <link rel="canonical" href={storeUrl} key="link_canonical" />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(storeSchema),
          }}
        />
      </Head>
      <div>
        <BreadcrumbsRenderer
          breadcrumbLabel={displayName}
          schemaItems={breadcrumbItems.map((breadcrumb, index) => {
            return {
              '@type': 'ListItem',
              position: index + 1,
              name: breadcrumb.text,
              item:
                index + 1 === breadcrumbItems.length
                  ? storeUrl
                  : originUrl.concat(breadcrumb.url),
            }
          })}
          breadcrumbs={breadcrumbItems}
          capitalizeText
        />
      </div>
      <PageWrapper compactMargin={true}>
        <GridItem colSpan={12}>
          <StoreDetailsInformation
            store={{
              ...props.store,
              status: isStoreOpen(props.store.openingTimes),
            }}
            i18nText={storeDetailsI18nText}
            apiKey={geocodeApiKeyStoreMap}
            storeDetailsRoute={Routes.StoreDetails}
            getDirectionsHref={openGoogleDirectionMap(
              `${props.store?.latitude},${props.store?.longitude}`,
            )}
          />
          {props.componentWidgets && (
            <WidgetRenderer widgets={props.componentWidgets} />
          )}
          {props.store.nearbyStores && (
            <StoreDetailsNearbyStores
              nearbyStores={props.store.nearbyStores.map((store) => {
                return {
                  ...store,
                  status: isStoreOpen(store.openingTimes),
                }
              })}
              i18nText={nearbyStoresI18nText}
              storeDetailsHref={(urlTag, town) => {
                return Routes.StoreDetails.replace(':town', town).replace(
                  ':store',
                  urlTag,
                )
              }}
            />
          )}
        </GridItem>
      </PageWrapper>
    </React.Fragment>
  )
}

export default StoreDetails

StoreDetails.Layout = FullHeaderLayout

StoreDetails.pageType = PageType.STORE_LOCATOR

export const getServerSideProps = withCacheConfiguration<
  {
    props: StoreDetailsProps
  },
  GetServerSidePropsContext
>(async (context) => {
  const nextConfig = getConfig() as NextConfig
  const request = context.req as EnterpriseRequest
  const config = await loadConfiguration(nextConfig, request)
  const apollo = createApolloWithRequest(request, config, true)

  const horizonFeatures = request.horizonFeatures

  const { publicRuntimeConfig } = config
  const { siteDefinition } = publicRuntimeConfig

  const allowOverrides = request.headers['x-enterprise-allow-overrides'] === '1'
  const allowSitePropertyOverrides =
    request.headers['x-enterprise-allow-site-property-overrides'] === '1'

  const clickAndCollectEnabled = horizonFeatures?.includes(
    Feature.ClickAndCollect,
  )
  const shippingDestination = (request.config.sessionSettings
    ?.shippingDestination ||
    siteDefinition.defaultSessionSettings.shippingDestination) as Country
  const currency = (request.config.sessionSettings?.currency ||
    siteDefinition.defaultSessionSettings.currency) as Currency

  apollo.addResolvers(
    Resolvers(
      apollo,
      publicRuntimeConfig,
      allowOverrides,
      allowSitePropertyOverrides,
      request.config.enableVary,
    ),
  )

  if (context.params) {
    const { data } = await apollo.query<{
      storeByStoreUrlTag: StoreDetailsData
      componentWidgets: ComponentWidgetsData['componentWidgets']
    }>({
      query: STORE_DETAILS_QUERY,
      fetchPolicy: 'no-cache',
      variables: {
        urlTag: context.params.store,
        name: ComponentName.STORE_LOCATOR,
        currency: currency as Currency,
        shippingDestination: shippingDestination as Country,
        clickAndCollectEnabled: clickAndCollectEnabled ?? false,
      },
    })
    return {
      props: {
        store: data?.storeByStoreUrlTag?.store || {},
        componentWidgets: data?.componentWidgets || [],
      },
    }
  }

  return {
    props: { componentWidgets: [] },
  }
})
